import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Form,
  FormGroup,
  Input,
  Label,
} from "reactstrap";

import { useFetchLocationsQuery } from "../api/cmsPanelsApi";
import { useCreateExchangeMutation } from "../api/exchangesApi";
import { getApiErrorMessage } from "../api/errorUtils";
import {
  useLazySearchReturnSalesQuery,
  useLookupReturnMutation,
} from "../api/returnsApi";
import { useScanSaleItemMutation, useSearchSaleItemsMutation } from "../api/salesApi";
import SaleItemResultRow from "../components/Sales/SaleItemResultRow";
import { useTranslation } from "../hooks/useTranslation";
import {
  buildItemOptionChips,
  pickItemDisplayFields,
  resolveItemImage,
} from "./saleItemPresentation";
import {
  buildSaleItemDisplayLabel,
  formatMoney,
  formatSaleDateTime,
  inferReturnableSaleLookupPayload,
  isExactSaleNumberQuery,
  shouldRunHistoricalSaleSearch,
  useDebounce,
} from "./salesUtils";

const inferLookupPayload = inferReturnableSaleLookupPayload;
const REPLACEMENT_SEARCH_PAGE_SIZE = 20;

function buildBasketContext(lines) {
  return {
    aggregateLines: lines
      .filter((line) => line.inventoryMode === "aggregate")
      .map((line) => ({
        variantId: line.variantId,
        quantity: line.quantity,
      })),
    serializedUnitBarcodes: lines.flatMap((line) =>
      (line.serializedUnits || []).map((unit) => unit.barcode),
    ),
  };
}

const ExchangesPage = () => {
  const t = useTranslation();
  const returnLookupInputRef = useRef(null);
  const replacementScanInputRef = useRef(null);
  const [locationId, setLocationId] = useState("");
  const [returnLookupValue, setReturnLookupValue] = useState("");
  const [replacementScanValue, setReplacementScanValue] = useState("");
  const [settlementMethod, setSettlementMethod] = useState("CASH");
  const [bankName, setBankName] = useState("");
  const [feedback, setFeedback] = useState(null);
  const [returnLookupResult, setReturnLookupResult] = useState(null);
  const [aggregateReturnSelections, setAggregateReturnSelections] = useState(
    {},
  );
  const [serializedReturnSelections, setSerializedReturnSelections] = useState(
    {},
  );
  const [replacementBasket, setReplacementBasket] = useState([]);
  const [completedExchange, setCompletedExchange] = useState(null);
  const [saleSearchResults, setSaleSearchResults] = useState([]);
  const [saleSearchTotal, setSaleSearchTotal] = useState(0);
  const [saleSearchQuery, setSaleSearchQuery] = useState("");
  const saleSearchRequestIdRef = useRef(0);
  // An exact sale number with nothing left to return, shown as a disabled row.
  const [exhaustedExactSale, setExhaustedExactSale] = useState(null);
  // Replacement product: scan (default) or manual search, like the Sales page.
  const [replacementMode, setReplacementMode] = useState("scan");
  const [replacementSearchValue, setReplacementSearchValue] = useState("");
  const [replacementSearchResults, setReplacementSearchResults] = useState([]);
  const [replacementSearchPagination, setReplacementSearchPagination] = useState(null);
  const replacementSearchInputRef = useRef(null);
  const replacementSearchRequestIdRef = useRef(0);

  const { data: locations = [], isFetching: isFetchingLocations } =
    useFetchLocationsQuery();
  const [lookupReturn, { isLoading: isLookingUpReturn }] =
    useLookupReturnMutation();
  const [searchReturnSales, { isFetching: isSearchingSales }] =
    useLazySearchReturnSalesQuery();
  const [scanSaleItem, { isLoading: isScanningReplacement }] =
    useScanSaleItemMutation();
  const [searchSaleItems, { isLoading: isSearchingReplacement }] =
    useSearchSaleItemsMutation();
  const [createExchange, { isLoading: isCreatingExchange }] =
    useCreateExchangeMutation();
  const debouncedReturnLookupValue = useDebounce(returnLookupValue, 350);

  const settlementOptions = useMemo(
    () => [
      { value: "CASH", label: t("commerceCommon.paymentCash"), icon: "icon-money-coins" },
      { value: "CARD", label: t("commerceCommon.paymentCard"), icon: "icon-credit-card" },
      { value: "BANK", label: t("commerceCommon.paymentBankTransfer"), icon: "icon-bank" },
    ],
    [t]
  );

  useEffect(() => {
    if (!locationId && locations.length) {
      setLocationId(String(locations[0].id));
    }
  }, [locationId, locations]);

  useEffect(() => {
    const frame = window.requestAnimationFrame(() => {
      if (completedExchange) {
        return;
      }

      if (!returnLookupResult) {
        returnLookupInputRef.current?.focus();
        returnLookupInputRef.current?.select?.();
        return;
      }

      replacementScanInputRef.current?.focus();
      replacementScanInputRef.current?.select?.();
    });

    return () => window.cancelAnimationFrame(frame);
  }, [
    completedExchange,
    returnLookupResult,
    replacementBasket.length,
    locationId,
  ]);

  useEffect(() => {
    if (returnLookupResult || completedExchange) {
      setSaleSearchResults([]);
      setSaleSearchTotal(0);
      setSaleSearchQuery("");
      return undefined;
    }

    const query = String(debouncedReturnLookupValue || "").trim();
    if (!shouldRunHistoricalSaleSearch(query)) {
      setSaleSearchResults([]);
      setSaleSearchTotal(0);
      setSaleSearchQuery("");
      return undefined;
    }

    const requestId = saleSearchRequestIdRef.current + 1;
    saleSearchRequestIdRef.current = requestId;

    searchReturnSales({
      q: query,
      page: 1,
      pageSize: 20,
    })
      .unwrap()
      .then((result) => {
        if (saleSearchRequestIdRef.current !== requestId) {
          return;
        }

        setSaleSearchQuery(result?.query || query);
        setSaleSearchResults(Array.isArray(result?.items) ? result.items : []);
        setSaleSearchTotal(Number(result?.total || 0));
      })
      .catch(() => {
        if (saleSearchRequestIdRef.current !== requestId) {
          return;
        }

        setSaleSearchResults([]);
        setSaleSearchTotal(0);
        setSaleSearchQuery(query);
      });

    return undefined;
  }, [
    completedExchange,
    debouncedReturnLookupValue,
    returnLookupResult,
    searchReturnSales,
  ]);

  const selectedReturnLines = useMemo(() => {
    if (!returnLookupResult?.lines?.length) {
      return [];
    }

    return returnLookupResult.lines
      .map((line) => {
        if (line.inventoryMode === "aggregate") {
          const quantity = Number(
            aggregateReturnSelections[line.saleLineId] || 0,
          );
          if (quantity <= 0) {
            return null;
          }

          return {
            ...line,
            returnedQuantity: quantity,
            refundAmount: Number(
              (
                Number(
                  (line.finalUnitPriceAmount ?? line.unitPriceAmount) || 0,
                ) * quantity
              ).toFixed(2),
            ),
            selectedUnits: [],
          };
        }

        const unitIds = serializedReturnSelections[line.saleLineId] || [];
        if (!unitIds.length) {
          return null;
        }

        const selectedUnits = (line.units || []).filter((unit) =>
          unitIds.includes(unit.saleLineUnitId),
        );

        return {
          ...line,
          returnedQuantity: unitIds.length,
          refundAmount: Number(
            (
              Number((line.finalUnitPriceAmount ?? line.unitPriceAmount) || 0) *
              unitIds.length
            ).toFixed(2),
          ),
          selectedUnits: selectedUnits.length
            ? selectedUnits
            : unitIds.map((saleLineUnitId) => ({ saleLineUnitId })),
        };
      })
      .filter(Boolean);
  }, [
    aggregateReturnSelections,
    returnLookupResult,
    serializedReturnSelections,
  ]);

  const returnTotal = useMemo(
    () =>
      selectedReturnLines.reduce(
        (total, line) => total + Number(line.refundAmount || 0),
        0,
      ),
    [selectedReturnLines],
  );

  const replacementSubtotal = useMemo(
    () =>
      replacementBasket.reduce(
        (total, line) => total + Number(line.lineSubtotalAmount || 0),
        0,
      ),
    [replacementBasket],
  );

  const settlementDifference = useMemo(
    () => Number((replacementSubtotal - returnTotal).toFixed(2)),
    [replacementSubtotal, returnTotal],
  );

  const settlementDirection = useMemo(() => {
    if (settlementDifference === 0) {
      return "even";
    }

    return settlementDifference > 0 ? "collect" : "refund";
  }, [settlementDifference]);

  const canScanReplacement = Boolean(
    returnLookupResult && selectedReturnLines.length,
  );

  const setWorkerFeedback = (type, message) => {
    setFeedback({
      type,
      message,
      id: `${Date.now()}-${Math.random()}`,
    });
  };

  const handleReturnLookupChange = (value) => {
    setReturnLookupValue(value);
    setExhaustedExactSale(null);
  };

  const resetReplacementSearch = () => {
    setReplacementSearchValue("");
    setReplacementSearchResults([]);
    setReplacementSearchPagination(null);
  };

  const resetReturnLookupField = () => {
    setReturnLookupValue("");
    setSaleSearchResults([]);
    setSaleSearchTotal(0);
    setSaleSearchQuery("");
    window.requestAnimationFrame(() => {
      returnLookupInputRef.current?.focus();
      returnLookupInputRef.current?.select?.();
    });
  };

  const clearSaleSearchResults = () => {
    setSaleSearchResults([]);
    setSaleSearchTotal(0);
    setSaleSearchQuery("");
  };

  const resetReplacementScanField = () => {
    setReplacementScanValue("");
    window.requestAnimationFrame(() => {
      replacementScanInputRef.current?.focus();
      replacementScanInputRef.current?.select?.();
    });
  };

  const hydrateReturnSelectionsFromLookup = (result) => {
    const nextAggregateSelections = {};
    const nextSerializedSelections = {};

    for (const line of result?.lines || []) {
      if (line.inventoryMode === "aggregate") {
        nextAggregateSelections[line.saleLineId] = 0;
        continue;
      }

      const selectedUnits = (line.units || [])
        .filter((unit) => unit.selected)
        .map((unit) => unit.saleLineUnitId);

      if (selectedUnits.length) {
        nextSerializedSelections[line.saleLineId] = selectedUnits;
      }
    }

    setAggregateReturnSelections(nextAggregateSelections);
    setSerializedReturnSelections(nextSerializedSelections);
  };

  const addScannedReplacementToBasket = (resolution) => {
    const item = resolution.item;
    if (!item) {
      return;
    }

    setReplacementBasket((current) => {
      if (item.inventoryMode === "aggregate") {
        const existing = current.find(
          (line) => line.variantId === item.variantId,
        );
        if (!existing) {
          return [
            ...current,
            {
              variantId: item.variantId,
              productId: item.productId,
              // Same display fields (incl. image) for scanned and searched items.
              ...pickItemDisplayFields(item),
              inventoryMode: item.inventoryMode,
              unitPriceAmount: Number(item.pricing?.unitPriceAmount || 0),
              quantity: 1,
              lineSubtotalAmount: Number(item.pricing?.unitPriceAmount || 0),
              serializedUnits: [],
            },
          ];
        }

        return current.map((line) =>
          line.variantId === item.variantId
            ? {
                ...line,
                quantity: line.quantity + 1,
                lineSubtotalAmount: Number(
                  (line.unitPriceAmount * (line.quantity + 1)).toFixed(2),
                ),
              }
            : line,
        );
      }

      const existing = current.find(
        (line) => line.variantId === item.variantId,
      );
      const nextUnit = item.inventoryUnit
        ? {
            id: item.inventoryUnit.id,
            barcode: item.inventoryUnit.barcode,
          }
        : null;

      if (!existing) {
        return [
          ...current,
          {
            variantId: item.variantId,
            productId: item.productId,
            // Same display fields (incl. image) for scanned and searched items.
            ...pickItemDisplayFields(item),
            inventoryMode: item.inventoryMode,
            unitPriceAmount: Number(item.pricing?.unitPriceAmount || 0),
            quantity: nextUnit ? 1 : 0,
            lineSubtotalAmount: Number(item.pricing?.unitPriceAmount || 0),
            serializedUnits: nextUnit ? [nextUnit] : [],
          },
        ];
      }

      return current.map((line) =>
        line.variantId === item.variantId
          ? {
              ...line,
              quantity: line.quantity + 1,
              lineSubtotalAmount: Number(
                (line.unitPriceAmount * (line.quantity + 1)).toFixed(2),
              ),
              serializedUnits: nextUnit
                ? [...line.serializedUnits, nextUnit]
                : line.serializedUnits,
            }
          : line,
      );
    });
  };

  const applyReturnLookupReadyResult = (result) => {
    setExhaustedExactSale(null);
    resetReplacementSearch();
    setReturnLookupResult(result);
    hydrateReturnSelectionsFromLookup(result);
    setReplacementBasket([]);
    setCompletedExchange(null);
    clearSaleSearchResults();
    setWorkerFeedback(
      "success",
      t("storeExchange.returnSideLoaded").replace(
        "{saleNumber}",
        result.sale?.saleNumber || ""
      ),
    );
  };

  const loadSaleForExchange = async (saleNumber, { clearInput = true } = {}) => {
    const normalizedSaleNumber = String(saleNumber || "").trim().toUpperCase();
    if (!normalizedSaleNumber) {
      return;
    }

    try {
      const result = await lookupReturn({
        saleNumber: normalizedSaleNumber,
      }).unwrap();
      if (result?.status === "ready") {
        applyReturnLookupReadyResult(result);
        if (clearInput) {
          setReturnLookupValue("");
        }
        return;
      }

      setReturnLookupResult(null);
      setAggregateReturnSelections({});
      setSerializedReturnSelections({});
      setReplacementBasket([]);
      setWorkerFeedback(
        result?.status === "not_found" ? "danger" : "warning",
        result?.message || t("storeReturn.nothingReturnable"),
      );
    } catch (error) {
      setReturnLookupResult(null);
      setAggregateReturnSelections({});
      setSerializedReturnSelections({});
      setReplacementBasket([]);
      setWorkerFeedback(
        "danger",
        getApiErrorMessage(error, t("storeExchange.returnSideLoadError"), {
          404: t("storeReturn.saleNotFound"),
        }),
      );
    }
  };

  const handleReturnLookupSubmit = async (event) => {
    event.preventDefault();

    const query = String(returnLookupValue || "").trim();
    setExhaustedExactSale(null);
    if (!query) {
      resetReturnLookupField();
      return;
    }

    // Exact sale number / barcode keep the fast existing lookup path.
    if (isExactSaleNumberQuery(query) || !shouldRunHistoricalSaleSearch(query)) {
      const payload = inferLookupPayload(query);
      if (!payload) {
        resetReturnLookupField();
        return;
      }

      try {
        const result = await lookupReturn(payload).unwrap();
        if (result?.status === "ready") {
          applyReturnLookupReadyResult(result);
        } else if (result?.status === "not_returnable" && result?.sale) {
          // The exact sale exists but nothing is left to exchange: identify it as
          // a disabled row instead of a toast that disappears.
          setReturnLookupResult(null);
          setAggregateReturnSelections({});
          setSerializedReturnSelections({});
          setReplacementBasket([]);
          setExhaustedExactSale({ sale: result.sale, saleLines: result.saleLines || [] });
        } else {
          setReturnLookupResult(null);
          setAggregateReturnSelections({});
          setSerializedReturnSelections({});
          setReplacementBasket([]);
          setWorkerFeedback(
            result?.status === "not_found" ? "danger" : "warning",
            result?.message || t("storeReturn.nothingReturnable"),
          );
        }
      } catch (error) {
        setReturnLookupResult(null);
        setAggregateReturnSelections({});
        setSerializedReturnSelections({});
        setReplacementBasket([]);
        setWorkerFeedback(
          "danger",
          getApiErrorMessage(error, t("storeExchange.returnSideLoadError"), {
            404: t("storeReturn.saleNotFound"),
          }),
        );
      } finally {
        resetReturnLookupField();
      }
      return;
    }

    // Broader text query: try barcode/SKU exact lookup first, then fall back to search results.
    try {
      const result = await lookupReturn({ inventoryUnitBarcode: query }).unwrap();
      if (result?.status === "ready") {
        applyReturnLookupReadyResult(result);
        setReturnLookupValue("");
        return;
      }
    } catch (_error) {
      // Fall through to historical sale search results.
    }

    if (saleSearchResults.length === 1) {
      await loadSaleForExchange(saleSearchResults[0]?.sale?.saleNumber);
      setReturnLookupValue("");
      return;
    }

    if (saleSearchResults.length > 1) {
      setWorkerFeedback("warning", t("storeReturn.chooseSaleFromSearch"));
      return;
    }

    try {
      const searchResult = await searchReturnSales({
        q: query,
        page: 1,
        pageSize: 20,
      }).unwrap();
      const items = Array.isArray(searchResult?.items) ? searchResult.items : [];
      setSaleSearchQuery(searchResult?.query || query);
      setSaleSearchResults(items);
      setSaleSearchTotal(Number(searchResult?.total || 0));

      if (items.length === 1) {
        await loadSaleForExchange(items[0]?.sale?.saleNumber);
        setReturnLookupValue("");
        return;
      }

      if (items.length > 1) {
        setWorkerFeedback("warning", t("storeReturn.chooseSaleFromSearch"));
        return;
      }

      setWorkerFeedback("warning", t("storeReturn.noMatchingSales"));
    } catch (error) {
      setWorkerFeedback(
        "danger",
        getApiErrorMessage(error, t("storeReturn.saleSearchError")),
      );
    }
  };

  const handleSelectSaleSearchResult = async (saleNumber) => {
    await loadSaleForExchange(saleNumber);
  };

  const handleReplacementScanSubmit = async (event) => {
    event.preventDefault();

    const barcode = String(replacementScanValue || "").trim();
    if (!locationId) {
      setWorkerFeedback(
        "warning",
        t("storeExchange.chooseLocationBeforeReplacementScan"),
      );
      resetReplacementScanField();
      return;
    }

    if (!canScanReplacement) {
      setWorkerFeedback(
        "warning",
        t("storeExchange.chooseReturnedThenScan"),
      );
      resetReplacementScanField();
      return;
    }

    if (!barcode) {
      resetReplacementScanField();
      return;
    }

    try {
      const result = await scanSaleItem({
        locationId: Number(locationId),
        barcode,
        basketContext: buildBasketContext(replacementBasket),
      }).unwrap();

      if (result?.status === "ready") {
        addScannedReplacementToBasket(result);
        setWorkerFeedback(
          "success",
          t("storeExchange.replacementAdded").replace(
            "{name}",
            result.item?.variantTitle ||
              result.item?.productTitle ||
              t("commerceCommon.itemFallback")
          ),
        );
      } else {
        setWorkerFeedback(
          result?.status === "not_found" ? "danger" : "warning",
          result?.message || t("storeExchange.replacementBarcodeInvalid"),
        );
      }
    } catch (error) {
      setWorkerFeedback(
        "danger",
        getApiErrorMessage(error, t("storeExchange.replacementScanError")),
      );
    } finally {
      resetReplacementScanField();
    }
  };

  const selectReplacementMode = (mode) => {
    setReplacementMode(mode);
    setReplacementSearchResults([]);
    setReplacementSearchPagination(null);
    window.requestAnimationFrame(() => {
      const input =
        mode === "search" ? replacementSearchInputRef.current : replacementScanInputRef.current;
      input?.focus();
      input?.select?.();
    });
  };

  const handleReplacementSearchSubmit = async (event) => {
    event.preventDefault();

    const query = String(replacementSearchValue || "").trim();
    if (!locationId) {
      setWorkerFeedback("warning", t("storeExchange.chooseLocationBeforeReplacementScan"));
      return;
    }

    if (!canScanReplacement) {
      setWorkerFeedback("warning", t("storeExchange.chooseReturnedThenScan"));
      return;
    }

    if (!query) {
      replacementSearchInputRef.current?.focus();
      return;
    }

    const requestId = replacementSearchRequestIdRef.current + 1;
    replacementSearchRequestIdRef.current = requestId;

    try {
      // Same endpoint and ordering as the Sales manual search: sellable first.
      const result = await searchSaleItems({
        locationId: Number(locationId),
        query,
        limit: REPLACEMENT_SEARCH_PAGE_SIZE,
        offset: 0,
      }).unwrap();

      if (replacementSearchRequestIdRef.current !== requestId) {
        return;
      }

      const items = result?.items || [];
      setReplacementSearchResults(items);
      setReplacementSearchPagination(result?.pagination || null);

      if (!items.length) {
        setWorkerFeedback("warning", t("storeExchange.replacementNoResults"));
      }
    } catch (error) {
      if (replacementSearchRequestIdRef.current !== requestId) {
        return;
      }
      setWorkerFeedback("danger", getApiErrorMessage(error, t("storeSales.searchError")));
    }
  };

  /**
   * Manual replacement add. Uses the same basket path as a scan, and the same
   * guards the Sales manual search applies: addable item, and never more of an
   * aggregate variant than the stock available at this location. The exchange
   * itself still validates stock on the server.
   */
  const handleAddReplacementSearchItem = (item) => {
    if (!canScanReplacement) {
      setWorkerFeedback("warning", t("storeExchange.chooseReturnedThenScan"));
      return;
    }

    if (!item?.canAddManually) {
      const reasonKey = {
        scan_unit_label: "storeSales.blockedScanUnitLabel",
        insufficient_stock: "storeSales.outOfStockAtLocation",
        missing_price: "storeSales.blockedMissingPrice",
      }[item?.reason];
      setWorkerFeedback("warning", t(reasonKey || "storeSales.cannotAddManually"));
      return;
    }

    const inBasket =
      replacementBasket.find((line) => line.variantId === item.variantId)?.quantity || 0;
    if (item.inventoryMode === "aggregate" && inBasket >= Number(item.availableQuantity || 0)) {
      setWorkerFeedback("warning", t("storeSales.outOfStockAtLocation"));
      return;
    }

    addScannedReplacementToBasket({ item });
    setWorkerFeedback(
      "success",
      t("storeExchange.replacementAdded").replace(
        "{name}",
        item.variantTitle || item.productTitle || t("commerceCommon.itemFallback"),
      ),
    );
  };

  const adjustAggregateReturnQuantity = (saleLineId, delta, max) => {
    setAggregateReturnSelections((current) => {
      const currentValue = Number(current[saleLineId] || 0);
      const nextValue = Math.max(0, Math.min(max, currentValue + delta));
      return {
        ...current,
        [saleLineId]: nextValue,
      };
    });
  };

  const handleRemoveReplacementOne = (variantId) => {
    setReplacementBasket((current) =>
      current.flatMap((line) => {
        if (line.variantId !== variantId) {
          return [line];
        }

        if (line.inventoryMode === "aggregate") {
          if (line.quantity <= 1) {
            return [];
          }

          return [
            {
              ...line,
              quantity: line.quantity - 1,
              lineSubtotalAmount: Number(
                (line.unitPriceAmount * (line.quantity - 1)).toFixed(2),
              ),
            },
          ];
        }

        const nextUnits = line.serializedUnits.slice(0, -1);
        if (!nextUnits.length) {
          return [];
        }

        return [
          {
            ...line,
            quantity: nextUnits.length,
            serializedUnits: nextUnits,
            lineSubtotalAmount: Number(
              (line.unitPriceAmount * nextUnits.length).toFixed(2),
            ),
          },
        ];
      }),
    );
  };

  const handleRemoveReplacementLine = (variantId) => {
    setReplacementBasket((current) =>
      current.filter((line) => line.variantId !== variantId),
    );
  };

  const handleCompleteExchange = async () => {
    if (!locationId) {
      setWorkerFeedback(
        "warning",
        t("storeExchange.chooseLocationBeforeComplete"),
      );
      return;
    }

    if (!returnLookupResult?.sale?.id) {
      setWorkerFeedback(
        "warning",
        t("storeExchange.loadReturnSideBeforeComplete"),
      );
      return;
    }

    if (!selectedReturnLines.length) {
      setWorkerFeedback("warning", t("storeExchange.chooseReturnedItemFirst"));
      return;
    }

    if (!replacementBasket.length) {
      setWorkerFeedback("warning", t("storeExchange.scanReplacementFirst"));
      return;
    }

    if (settlementDirection !== "even" && !settlementMethod) {
      setWorkerFeedback(
        "warning",
        t("storeExchange.chooseSettlementMethod"),
      );
      return;
    }

    try {
      const result = await createExchange({
        originalSaleId: returnLookupResult.sale.id,
        locationId: Number(locationId),
        returnSelection: selectedReturnLines.map((line) => ({
          saleLineId: line.saleLineId,
          quantity:
            line.inventoryMode === "aggregate"
              ? line.returnedQuantity
              : undefined,
          saleLineUnitIds:
            line.inventoryMode === "serialized"
              ? line.selectedUnits.map((unit) => unit.saleLineUnitId)
              : undefined,
        })),
        replacementLines: replacementBasket.map((line) => ({
          catalogVariantId: line.variantId,
          quantity:
            line.inventoryMode === "aggregate" ? line.quantity : undefined,
          serializedUnitBarcodes:
            line.inventoryMode === "serialized"
              ? line.serializedUnits.map((unit) => unit.barcode)
              : undefined,
        })),
        settlementMethod:
          settlementDirection === "even" ? undefined : settlementMethod,
        bankName:
          settlementDirection === "even" || settlementMethod === "CASH"
            ? undefined
            : String(bankName || "").trim() || undefined,
      }).unwrap();

      setCompletedExchange(result);
      setReturnLookupResult(null);
      setAggregateReturnSelections({});
      setSerializedReturnSelections({});
      setReplacementBasket([]);
      setBankName("");
      setFeedback(null);
    } catch (error) {
      setWorkerFeedback(
        "danger",
        getApiErrorMessage(error, t("storeExchange.completeExchangeError"), {
          409: t("storeExchange.exchangeInvalidReload"),
        }),
      );
    }
  };

  const startNewExchange = () => {
    setCompletedExchange(null);
    setReturnLookupResult(null);
    setAggregateReturnSelections({});
    setSerializedReturnSelections({});
    setReplacementBasket([]);
    setExhaustedExactSale(null);
    resetReplacementSearch();
    setSettlementMethod("CASH");
    setBankName("");
    setFeedback(null);
    resetReturnLookupField();
  };

  const clearExchangeContext = () => {
    setReturnLookupResult(null);
    setAggregateReturnSelections({});
    setSerializedReturnSelections({});
    setReplacementBasket([]);
    setExhaustedExactSale(null);
    resetReplacementSearch();
    setFeedback(null);
    resetReturnLookupField();
  };

  // Option chips come from the shared presentation helper (same normalization as
  // Sales / Returns; the brand is deliberately not a chip).
  const buildOptionChips = (optionSummary) => buildItemOptionChips(optionSummary, t);

  const buildItemTitle = (line) =>
    buildSaleItemDisplayLabel({
      productTitle: line.productTitle,
      variantTitle: line.variantTitle,
      optionSummary: line.optionSummary,
    });

  // Real catalog image (or a neutral tile) - never a made-up picture.
  const renderItemThumb = (line) => {
    const image = resolveItemImage(line);

    return (
      <span className="sop-thumb" aria-hidden="true">
        {image ? <img src={image} alt="" loading="lazy" /> : null}
      </span>
    );
  };

  const renderExhaustedExactSale = () => {
    const { sale, saleLines } = exhaustedExactSale;
    const line = saleLines[0];
    const chips = line ? buildOptionChips(line.optionSummary) : [];

    return (
      <div className="xg-search-results" aria-live="polite">
        <div className="xg-result-row is-disabled is-informational" aria-disabled="true">
          {line ? renderItemThumb(line) : null}
          <div className="xg-result-row__main">
            <div className="xg-result-row__top">
              <span className="xg-result-row__number">{sale.saleNumber}</span>
              <span className="xg-result-row__datetime">
                {formatSaleDateTime(sale.completedAt)}
              </span>
            </div>
            {line && <span className="xg-item-title">{buildItemTitle(line)}</span>}
            {chips.length > 0 && (
              <div className="xg-item-chips">
                {chips.map((chip) => (
                  <span className="xg-item-chip" key={chip}>
                    {chip}
                  </span>
                ))}
              </div>
            )}
            {sale.location?.name && (
              <span className="xg-result-row__location">{sale.location.name}</span>
            )}
          </div>
          <div className="xg-result-row__side">
            <span className="xg-result-row__total">{formatMoney(sale.totalAmount)}</span>
            <span className="xg-result-row__exhausted">
              {t("storeExchange.saleFullyReturned")}
            </span>
          </div>
        </div>
      </div>
    );
  };

  const renderReplacementSearchItem = (item) => {
    const isAggregate = item.inventoryMode === "aggregate";
    const availableQuantity = Number(item.availableQuantity || 0);
    const inBasket =
      replacementBasket.find((line) => line.variantId === item.variantId)?.quantity || 0;
    const atStockLimit = isAggregate && item.canAddManually && inBasket >= availableQuantity;
    const isAddable = Boolean(item.canAddManually) && !atStockLimit && canScanReplacement;

    let stockLabel = t("storeSales.outOfStock");
    let stockTone = "empty";
    if (!isAggregate) {
      stockLabel = t("commerceCommon.scanLabel");
      stockTone = "neutral";
    } else if (availableQuantity > 0) {
      stockLabel = t("storeSales.inStockCount").replace("{count}", availableQuantity);
      stockTone = "ok";
    }

    let actionLabel = t("storeSales.outOfStockShort");
    if (atStockLimit) {
      actionLabel = t("storeSales.allInBasket");
    } else if (item.canAddManually) {
      actionLabel = t("storeExchange.addReplacement");
    } else if (!isAggregate) {
      actionLabel = t("commerceCommon.scanLabel");
    }

    return (
      <SaleItemResultRow
        key={item.variantId}
        item={item}
        title={item.productTitle || buildItemTitle(item)}
        chips={buildOptionChips(item.optionSummary)}
        sku={item.sku}
        price={formatMoney(item.pricing?.unitPriceAmount)}
        stockLabel={stockLabel}
        stockTone={stockTone}
        actionLabel={actionLabel}
        isAddable={isAddable}
        actionDisabled={isCreatingExchange}
        doubleClickHint={t("storeSales.doubleClickToAdd")}
        onAdd={handleAddReplacementSearchItem}
      />
    );
  };

  const renderFeedback = () => {
    if (!feedback) {
      return null;
    }

    const isSuccess = feedback.type === "success";

    return (
      <div
        className={`xg-feedback xg-feedback--${feedback.type}`}
        key={feedback.id}
        role={isSuccess ? "status" : "alert"}
      >
        <i
          className={`tim-icons ${
            isSuccess ? "icon-check-2" : "icon-alert-circle-exc"
          } xg-feedback__icon`}
          aria-hidden="true"
        />
        <p className="xg-feedback__text">{feedback.message}</p>
      </div>
    );
  };

  // Purely presentational: every value below is derived from existing state
  // (returnLookupResult / selectedReturnLines / replacementBasket /
  // completedExchange / saleSearchResults), no new business logic.
  const renderStepIndicator = () => {
    const step1Done = Boolean(returnLookupResult);
    const step2Done = Boolean(selectedReturnLines.length);
    const step3Done = Boolean(replacementBasket.length);

    const step1Hint = step1Done
      ? t("storeExchange.stepFindSaleDoneHint")
      : saleSearchResults.length > 0
      ? t("storeExchange.stepFindSalePickHint")
      : t("storeExchange.stepFindSaleHint");

    const step2Hint = step2Done
      ? selectedReturnLines.length === 1
        ? t("storeExchange.productsSelectedCount").replace(
            "{count}",
            selectedReturnLines.length
          )
        : t("storeExchange.productsSelectedCountPlural").replace(
            "{count}",
            selectedReturnLines.length
          )
      : t("storeExchange.stepChooseReturnHint");

    const step3Hint = step3Done
      ? replacementBasket.length === 1
        ? t("storeExchange.productsAddedCount").replace(
            "{count}",
            replacementBasket.length
          )
        : t("storeExchange.productsAddedCountPlural").replace(
            "{count}",
            replacementBasket.length
          )
      : step2Done
      ? t("storeExchange.stepAddReplacementActiveHint")
      : t("storeExchange.stepAddReplacementPendingHint");

    const steps = [
      { label: t("storeExchange.stepFindSale"), hint: step1Hint, done: step1Done, active: !step1Done },
      {
        label: t("storeExchange.stepChooseReturn"),
        hint: step2Hint,
        done: step2Done,
        active: step1Done && !step2Done,
      },
      {
        label: t("storeExchange.stepAddReplacement"),
        hint: step3Hint,
        done: step3Done,
        active: step2Done && !step3Done,
      },
      {
        label: t("storeExchange.stepComplete"),
        hint: t("storeExchange.stepCompleteHint"),
        done: false,
        active: step3Done,
      },
    ];

    return (
      <ol className="xg-steps" aria-label={t("storeExchange.subtitle")}>
        {steps.map((step, index) => (
          <li
            key={step.label}
            className={`xg-step${step.done ? " is-done" : ""}${
              step.active ? " is-active" : ""
            }`}
          >
            <span className="xg-step__marker" aria-hidden="true">
              {step.done ? <i className="tim-icons icon-check-2" /> : index + 1}
            </span>
            <span className="xg-step__text">
              <span className="xg-step__label">{step.label}</span>
              <span className="xg-step__hint">{step.hint}</span>
            </span>
          </li>
        ))}
      </ol>
    );
  };

  const renderSaleSearchResult = (item) => {
    const matchedLine = item.matchedLines?.[0];
    const title = matchedLine ? buildItemTitle(matchedLine) : null;
    const chips = matchedLine ? buildOptionChips(matchedLine.optionSummary) : [];
    const isExhausted = item.hasReturnableMatchedLines === false;

    return (
      <button
        key={item.sale?.id || item.sale?.saleNumber}
        type="button"
        className={`xg-result-row${isExhausted ? " is-disabled" : ""}`}
        onClick={() => handleSelectSaleSearchResult(item.sale?.saleNumber)}
        disabled={isLookingUpReturn || isCreatingExchange || isExhausted}
      >
        {matchedLine ? renderItemThumb(matchedLine) : null}
        <div className="xg-result-row__main">
          <div className="xg-result-row__top">
            <span className="xg-result-row__number">{item.sale?.saleNumber}</span>
            <span className="xg-result-row__datetime">
              {formatSaleDateTime(item.sale?.completedAt)}
            </span>
          </div>
          {title && (
            <>
              <span className="xg-item-title">{title}</span>
              {chips.length > 0 && (
                <div className="xg-item-chips">
                  {chips.map((chip) => (
                    <span className="xg-item-chip" key={chip}>
                      {chip}
                    </span>
                  ))}
                </div>
              )}
            </>
          )}
          {item.sale?.location?.name && (
            <span className="xg-result-row__location">{item.sale.location.name}</span>
          )}
        </div>

        <div className="xg-result-row__side">
          <span className="xg-result-row__total">
            {formatMoney(matchedLine?.lineTotalAmount ?? item.sale?.totalAmount)}
          </span>
          {isExhausted ? (
            <span className="xg-result-row__exhausted">
              {t("storeExchange.saleFullyReturned")}
            </span>
          ) : (
            matchedLine && (
              <span className="xg-result-row__returnable">
                {t("storeExchange.returnableUnitsShort").replace(
                  "{count}",
                  matchedLine.returnableQuantity
                )}
              </span>
            )
          )}
        </div>

        {!isExhausted && (
          <i className="tim-icons icon-minimal-right xg-result-row__chevron" aria-hidden="true" />
        )}
      </button>
    );
  };

  const renderReturnableRow = (line) => {
    const isAggregate = line.inventoryMode === "aggregate";
    const selectedQuantity = isAggregate
      ? Number(aggregateReturnSelections[line.saleLineId] || 0)
      : (serializedReturnSelections[line.saleLineId] || []).length;
    const chips = buildOptionChips(line.optionSummary);
    const refundAmount =
      Number((line.finalUnitPriceAmount ?? line.unitPriceAmount) || 0) * selectedQuantity;

    return (
      <div
        key={line.saleLineId}
        className={`xg-item-row${selectedQuantity > 0 ? " is-selected" : ""}`}
      >
        <div className="xg-item-row__main has-media">
          {renderItemThumb(line)}
          <span className="xg-item-title">{buildItemTitle(line)}</span>
          {line.sku && <span className="xg-item-sku sp-truncate">{line.sku}</span>}
          {chips.length > 0 && (
            <div className="xg-item-chips">
              {chips.map((chip) => (
                <span className="xg-item-chip" key={chip}>
                  {chip}
                </span>
              ))}
            </div>
          )}
          <span className="xg-item-can-return">
            {t("storeExchange.canReturnCount").replace("{count}", line.returnableQuantity)}
          </span>
          <span className="xg-item-meta">
            {t("storeReturn.soldQty")}: {line.soldQuantity ?? line.returnableQuantity} ·{" "}
            {t("storeReturn.returnedQty")}: {line.alreadyReturnedQuantity || 0}
          </span>
        </div>

        <div className="xg-item-row__control">
          {isAggregate ? (
            <div className="xg-qty-stepper">
              <button
                type="button"
                className="xg-qty-btn"
                onClick={() =>
                  adjustAggregateReturnQuantity(line.saleLineId, -1, line.returnableQuantity)
                }
                disabled={isCreatingExchange || selectedQuantity <= 0}
                aria-label={t("storeSales.decreaseQty")}
              >
                <span aria-hidden="true">−</span>
              </button>
              <span className="xg-qty-value">{selectedQuantity}</span>
              <button
                type="button"
                className="xg-qty-btn"
                onClick={() =>
                  adjustAggregateReturnQuantity(line.saleLineId, 1, line.returnableQuantity)
                }
                disabled={isCreatingExchange || selectedQuantity >= line.returnableQuantity}
                aria-label={t("storeSales.increaseQty")}
              >
                <span aria-hidden="true">+</span>
              </button>
            </div>
          ) : (
            <span className="xg-scan-hint">
              {line.requiresSoldUnitScan
                ? t("commerceCommon.scanSoldLabelHint")
                : t("commerceCommon.selectedCount").replace("{count}", selectedQuantity)}
            </span>
          )}
        </div>

        <span className="xg-item-row__value">{formatMoney(refundAmount)}</span>
      </div>
    );
  };

  const renderReplacementRow = (line) => {
    const isAggregate = line.inventoryMode === "aggregate";
    const chips = buildOptionChips(line.optionSummary);

    return (
      <div className="xg-item-row xg-item-row--replacement" key={line.variantId}>
        <div className="xg-item-row__main has-media">
          {renderItemThumb(line)}
          <span className="xg-item-title">{buildItemTitle(line)}</span>
          {line.sku && <span className="xg-item-sku sp-truncate">{line.sku}</span>}
          {chips.length > 0 && (
            <div className="xg-item-chips">
              {chips.map((chip) => (
                <span className="xg-item-chip" key={chip}>
                  {chip}
                </span>
              ))}
            </div>
          )}
          {isAggregate && (
            <span className="xg-item-meta">{t("storeExchange.scanAgainForMoreHint")}</span>
          )}
        </div>

        <div className="xg-item-row__control">
          <div className="xg-qty-stepper">
            <button
              type="button"
              className="xg-qty-btn"
              onClick={() => handleRemoveReplacementOne(line.variantId)}
              disabled={isCreatingExchange}
              aria-label={t("storeSales.decreaseQty")}
            >
              <span aria-hidden="true">−</span>
            </button>
            <span className="xg-qty-value">{line.quantity}</span>
          </div>
        </div>

        <span className="xg-item-row__value">{formatMoney(line.lineSubtotalAmount)}</span>

        <Button
          color="danger"
          size="sm"
          className="xg-icon-btn"
          onClick={() => handleRemoveReplacementLine(line.variantId)}
          disabled={isCreatingExchange}
          aria-label={`${t("storeReturn.clearLineSelection")} — ${buildItemTitle(line)}`}
          title={t("storeReturn.clearLineSelection")}
        >
          <i className="tim-icons icon-simple-remove" aria-hidden="true" />
        </Button>
      </div>
    );
  };

  const renderSettlementPanel = (sticky = true) => {
    const hasReturnSelection = selectedReturnLines.length > 0;
    const isEven = settlementDirection === "even";
    const isCollect = settlementDirection === "collect";
    const resultLabel = isEven
      ? t("storeExchange.noDifferenceLabel")
      : isCollect
      ? t("storeExchange.customerMustPayLabel")
      : t("storeExchange.customerMustBeRefundedLabel");
    const resultValue = isEven
      ? formatMoney(0)
      : formatMoney(Math.abs(settlementDifference));

    return (
      <aside
        className={`exchange-page-checkout${sticky ? " exchange-page-checkout--sticky" : ""}`}
        aria-label={t("storeExchange.exchangeSummaryTitle")}
      >
        <div className="exchange-page-checkout__inner xg-checkout">
          <h2 className="xg-checkout__title">{t("storeExchange.exchangeSummaryTitle")}</h2>

          <div className="xg-checkout__values">
            <div className="xg-checkout__value-row">
              <span>{t("storeExchange.returnedProductValueLabel")}</span>
              <span>{formatMoney(returnTotal)}</span>
            </div>
            <div className="xg-checkout__value-row">
              <span>{t("storeExchange.newProductValueLabel")}</span>
              <span>{formatMoney(replacementSubtotal)}</span>
            </div>
          </div>

          {hasReturnSelection ? (
            <div className={`xg-result xg-result--${settlementDirection}`}>
              <span className="xg-result__label">{resultLabel}</span>
              <span className="xg-result__value">{resultValue}</span>
              {isEven && <span className="xg-result__hint">{t("storeExchange.noDifferenceHint")}</span>}
            </div>
          ) : (
            <div className="xg-result">
              <span className="xg-result__hint">
                {t("storeExchange.selectReturnToSeeDifferenceHint")}
              </span>
            </div>
          )}

          {hasReturnSelection && !isEven && (
            <div className="xg-checkout__payment">
              <span className="sp-label">
                {isCollect
                  ? t("storeExchange.paymentMethodContextLabel")
                  : t("storeExchange.refundMethodContextLabel")}
              </span>
              <div
                className="xg-payment-options"
                role="radiogroup"
                aria-label={
                  isCollect
                    ? t("storeExchange.paymentMethodContextLabel")
                    : t("storeExchange.refundMethodContextLabel")
                }
              >
                {settlementOptions.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    role="radio"
                    aria-checked={settlementMethod === option.value}
                    className={`xg-payment-option${
                      settlementMethod === option.value ? " is-active" : ""
                    }`}
                    onClick={() => setSettlementMethod(option.value)}
                    disabled={isCreatingExchange || Boolean(completedExchange)}
                  >
                    <i className={`tim-icons ${option.icon}`} aria-hidden="true" />
                    <span>{option.label}</span>
                  </button>
                ))}
              </div>
              {settlementMethod !== "CASH" && (
                <FormGroup className="xg-bank-field">
                  <Label for="exchange-bank-name">{t("commerceCommon.bankProvider")}</Label>
                  <Input
                    id="exchange-bank-name"
                    type="text"
                    placeholder={t("commerceCommon.optional")}
                    value={bankName}
                    onChange={(event) => setBankName(event.target.value)}
                    disabled={isCreatingExchange || Boolean(completedExchange)}
                  />
                </FormGroup>
              )}
            </div>
          )}

          <Button
            color="success"
            className="xg-complete-btn"
            onClick={handleCompleteExchange}
            disabled={
              !selectedReturnLines.length ||
              !replacementBasket.length ||
              isCreatingExchange ||
              Boolean(completedExchange)
            }
          >
            {isCreatingExchange
              ? t("commerceCommon.completing")
              : t("storeExchange.completeExchange")}
          </Button>
        </div>
      </aside>
    );
  };

  return (
    <div className="content sales-premium sales-page exchange-page">
      <Card className="sales-page-card">
        <CardHeader className="sales-page-header">
          <div>
            <div className="sp-title">{t("storeExchange.title")}</div>
            <p className="sp-subtitle">
              {completedExchange
                ? t("storeExchange.exchangeSaved").replace(
                    "{exchangeNumber}",
                    completedExchange.exchangeNumber
                  )
                : t("storeExchange.subtitle")}
            </p>
          </div>
          <div className="sales-page-header__actions">
            {completedExchange ? null : returnLookupResult ? (
              <>
                {replacementBasket.length > 0 ? (
                  <Button
                    color="secondary"
                    size="sm"
                    onClick={() => setReplacementBasket([])}
                    disabled={isCreatingExchange}
                  >
                    {t("storeExchange.clearReplacement")}
                  </Button>
                ) : null}
                <Button
                  color="secondary"
                  size="sm"
                  onClick={clearExchangeContext}
                  disabled={isCreatingExchange}
                >
                  {t("commerceCommon.clearLookup")}
                </Button>
              </>
            ) : null}
          </div>
        </CardHeader>

        <CardBody className="sales-page-body">
          {completedExchange ? (
            <div className="xg-success">
              <div className="xg-success__badge" aria-hidden="true">
                <i className="tim-icons icon-check-2" />
              </div>
              <h2 className="xg-success__title">{t("storeExchange.exchangeCompletedTitle")}</h2>
              <p className="xg-success__subtitle">
                {t("storeExchange.exchangeSaved").replace(
                  "{exchangeNumber}",
                  completedExchange.exchangeNumber
                )}
              </p>

              <div className="xg-success__facts">
                <div className="xg-fact">
                  <span className="xg-fact__icon">
                    <i className="tim-icons icon-notes" aria-hidden="true" />
                  </span>
                  <span className="xg-fact__body">
                    <span className="xg-fact__label">{t("commerceCommon.exchangeNumber")}</span>
                    <span className="xg-fact__value">{completedExchange.exchangeNumber}</span>
                  </span>
                </div>
                {completedExchange.originalSale?.saleNumber && (
                  <div className="xg-fact">
                    <span className="xg-fact__icon">
                      <i className="tim-icons icon-notes" aria-hidden="true" />
                    </span>
                    <span className="xg-fact__body">
                      <span className="xg-fact__label">{t("commerceCommon.originalSale")}</span>
                      <span className="xg-fact__value">
                        {completedExchange.originalSale.saleNumber}
                      </span>
                    </span>
                  </div>
                )}
                {completedExchange.location?.name && (
                  <div className="xg-fact">
                    <span className="xg-fact__icon">
                      <i className="tim-icons icon-square-pin" aria-hidden="true" />
                    </span>
                    <span className="xg-fact__body">
                      <span className="xg-fact__label">{t("commerceCommon.location")}</span>
                      <span className="xg-fact__value">{completedExchange.location.name}</span>
                    </span>
                  </div>
                )}
                {completedExchange.completedAt && (
                  <div className="xg-fact">
                    <span className="xg-fact__icon">
                      <i className="tim-icons icon-calendar-60" aria-hidden="true" />
                    </span>
                    <span className="xg-fact__body">
                      <span className="xg-fact__label">{t("storeSales.dateAndTime")}</span>
                      <span className="xg-fact__value">
                        {formatSaleDateTime(completedExchange.completedAt)}
                      </span>
                    </span>
                  </div>
                )}
                <div className="xg-fact">
                  <span className="xg-fact__icon">
                    <i className="tim-icons icon-credit-card" aria-hidden="true" />
                  </span>
                  <span className="xg-fact__body">
                    <span className="xg-fact__label">{t("commerceCommon.balance")}</span>
                    <span className="xg-fact__value">
                      {completedExchange.settlementDirection === "even"
                        ? t("storeExchange.noDifferenceLabel")
                        : completedExchange.settlementDirection === "collect_from_customer"
                        ? t("storeExchange.customerPaidLabel")
                        : t("storeExchange.customerRefundedLabel")}
                    </span>
                  </span>
                </div>
              </div>

              <div
                className={`xg-result xg-success__result xg-result--${
                  completedExchange.settlementDirection === "even"
                    ? "even"
                    : completedExchange.settlementDirection === "collect_from_customer"
                    ? "collect"
                    : "refund"
                }`}
              >
                <span className="xg-result__label">{t("storeExchange.finalDifferenceLabel")}</span>
                <span className="xg-result__value xg-result__value--lg">
                  {formatMoney(completedExchange.settlementAmount)}
                </span>
                <span className="xg-result__hint">
                  {completedExchange.settlementDirection === "even"
                    ? t("commerceCommon.evenExchange")
                    : completedExchange.settlementDirection === "collect_from_customer"
                    ? t("storeExchange.customerPaidLabel")
                    : t("storeExchange.customerRefundedLabel")}
                </span>
              </div>

              <h3 className="xg-items-title">{t("commerceCommon.returnedItems")}</h3>
              <div className="xg-receipt-table">
                <div className="xg-receipt-row xg-receipt-row--header" aria-hidden="true">
                  <span>#</span>
                  <span>{t("commerceCommon.itemHeader")}</span>
                  <span>{t("commerceCommon.qty")}</span>
                  <span>{t("commerceCommon.price")}</span>
                  <span>{t("commerceCommon.refund")}</span>
                </div>
                {(completedExchange.return?.lines || []).map((line, index) => {
                  const chips = buildOptionChips(line.optionSummary);
                  return (
                    <div className="xg-receipt-row" key={line.id}>
                      <span className="xg-receipt-row__index">{index + 1}</span>
                      <div className="xg-receipt-row__item">
                        <span className="xg-item-title">{buildItemTitle(line)}</span>
                        {line.sku && <span className="xg-item-sku sp-truncate">{line.sku}</span>}
                        {(chips.length > 0 || line.brandName) && (
                          <div className="xg-item-chips">
                            {line.brandName && (
                              <span className="xg-item-chip">{line.brandName}</span>
                            )}
                            {chips.map((chip) => (
                              <span className="xg-item-chip" key={chip}>
                                {chip}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                      <span className="xg-receipt-row__qty">{line.returnedQuantity}</span>
                      <span className="xg-receipt-row__price">
                        {formatMoney(line.finalUnitPriceAmount ?? line.unitPriceAmount)}
                      </span>
                      <span className="xg-receipt-row__total">{formatMoney(line.refundAmount)}</span>
                    </div>
                  );
                })}
              </div>

              <h3 className="xg-items-title">{t("commerceCommon.replacementItems")}</h3>
              <div className="xg-receipt-table">
                <div className="xg-receipt-row xg-receipt-row--header" aria-hidden="true">
                  <span>#</span>
                  <span>{t("commerceCommon.itemHeader")}</span>
                  <span>{t("commerceCommon.qty")}</span>
                  <span>{t("commerceCommon.price")}</span>
                  <span>{t("commerceCommon.total")}</span>
                </div>
                {(completedExchange.replacementSale?.lines || []).map((line, index) => {
                  const chips = buildOptionChips(line.optionSummary);
                  return (
                    <div className="xg-receipt-row" key={line.id}>
                      <span className="xg-receipt-row__index">{index + 1}</span>
                      <div className="xg-receipt-row__item">
                        <span className="xg-item-title">{buildItemTitle(line)}</span>
                        {line.sku && <span className="xg-item-sku sp-truncate">{line.sku}</span>}
                        {(chips.length > 0 || line.brandName) && (
                          <div className="xg-item-chips">
                            {line.brandName && (
                              <span className="xg-item-chip">{line.brandName}</span>
                            )}
                            {chips.map((chip) => (
                              <span className="xg-item-chip" key={chip}>
                                {chip}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                      <span className="xg-receipt-row__qty">{line.quantity}</span>
                      <span className="xg-receipt-row__price">
                        {formatMoney(line.finalUnitPriceAmount ?? line.unitPriceAmount)}
                      </span>
                      <span className="xg-receipt-row__total">
                        {formatMoney(line.lineSubtotalAmount)}
                      </span>
                    </div>
                  );
                })}
              </div>

              <div className="xg-success__actions">
                <Button
                  color="success"
                  className="xg-success-action xg-success-action--primary"
                  onClick={startNewExchange}
                >
                  <i className="tim-icons icon-refresh-01" aria-hidden="true" />
                  <span className="xg-success-action__text">
                    <span className="xg-success-action__title">
                      {t("commerceCommon.newExchange")}
                    </span>
                    <span className="xg-success-action__hint">
                      {t("storeExchange.newExchangeHint")}
                    </span>
                  </span>
                </Button>
              </div>
            </div>
          ) : (
            <>
              {renderStepIndicator()}
              <div
                className={`exchange-page-layout${
                  returnLookupResult ? " exchange-page-layout--split" : ""
                }`}
              >
                <div className="exchange-page-main">
                  <section className="xg-search-card">
                    {renderFeedback()}

                    <div className="xg-search-card__top">
                      <FormGroup className="xg-location-field">
                        <Label for="exchange-location">{t("commerceCommon.location")}</Label>
                        <Input
                          id="exchange-location"
                          type="select"
                          value={locationId}
                          onChange={(event) => setLocationId(event.target.value)}
                          disabled={isFetchingLocations || isCreatingExchange}
                        >
                          <option value="">{t("commerceCommon.chooseLocation")}</option>
                          {locations.map((location) => (
                            <option key={location.id} value={location.id}>
                              {location.name ||
                                t("commerceCommon.locationFallback").replace(
                                  "{id}",
                                  location.id
                                )}
                            </option>
                          ))}
                        </Input>
                      </FormGroup>
                    </div>

                    {!returnLookupResult ? (
                      <Form className="xg-search-form" onSubmit={handleReturnLookupSubmit}>
                        <Label for="exchange-return-lookup" className="sr-only">
                          {t("storeReturn.saleLookupLabel")}
                        </Label>
                        <div className="xg-field-wrap">
                          <div className="xg-field-control">
                            <i
                              className="tim-icons icon-zoom-split xg-field-icon"
                              aria-hidden="true"
                            />
                            <Input
                              id="exchange-return-lookup"
                              innerRef={returnLookupInputRef}
                              type="text"
                              autoComplete="off"
                              className="xg-field-input"
                              placeholder={t("storeReturn.saleLookupPlaceholder")}
                              value={returnLookupValue}
                              onChange={(event) => handleReturnLookupChange(event.target.value)}
                              disabled={
                                !locationId ||
                                isLookingUpReturn ||
                                isSearchingSales ||
                                isCreatingExchange
                              }
                              aria-label={t("storeReturn.saleLookupLabel")}
                            />
                          </div>
                          <Button
                            color="primary"
                            type="submit"
                            className="xg-field-submit"
                            disabled={
                              !locationId ||
                              !returnLookupValue.trim() ||
                              isLookingUpReturn ||
                              isSearchingSales ||
                              isCreatingExchange
                            }
                          >
                            {isLookingUpReturn || isSearchingSales
                              ? t("commerceCommon.lookingUp")
                              : t("storeReturn.findSale")}
                          </Button>
                        </div>

                        {!returnLookupResult && exhaustedExactSale
                          ? renderExhaustedExactSale()
                          : null}

                        {!returnLookupResult && (saleSearchResults.length > 0 || saleSearchQuery) ? (
                          <div className="xg-search-results" aria-live="polite">
                            <p className="xg-search-results__count">
                              {isSearchingSales
                                ? t("commerceCommon.lookingUp")
                                : t("storeReturn.saleSearchResults")
                                    .replace(
                                      "{count}",
                                      String(saleSearchTotal || saleSearchResults.length)
                                    )
                                    .replace(
                                      "{query}",
                                      saleSearchQuery || returnLookupValue.trim()
                                    )}
                            </p>
                            {saleSearchResults.length ? (
                              <div className="xg-search-results__list sp-scrollbar-hidden">
                                {saleSearchResults.map(renderSaleSearchResult)}
                              </div>
                            ) : (
                              <p className="xg-search-results__empty">
                                {t("storeReturn.noMatchingSales")}
                              </p>
                            )}
                          </div>
                        ) : null}
                      </Form>
                    ) : (
                      <div className="xg-selected-sale">
                        <div className="xg-selected-sale__main">
                          <span className="xg-selected-sale__number">
                            {returnLookupResult.sale?.saleNumber}
                          </span>
                          {returnLookupResult.sale?.completedAt && (
                            <span className="xg-selected-sale__datetime">
                              {formatSaleDateTime(returnLookupResult.sale.completedAt)}
                            </span>
                          )}
                        </div>
                        <div className="xg-selected-sale__facts">
                          {returnLookupResult.sale?.location?.name && (
                            <span className="xg-selected-sale__fact">
                              <i className="tim-icons icon-square-pin" aria-hidden="true" />
                              {returnLookupResult.sale.location.name}
                            </span>
                          )}
                          <span className="xg-selected-sale__fact xg-selected-sale__fact--total">
                            {formatMoney(returnLookupResult.sale?.totalAmount)}
                          </span>
                        </div>
                      </div>
                    )}
                  </section>

                  {returnLookupResult ? (
                    <>
                      <section className="xg-items-card">
                        <div className="xg-items-card__head">
                          <h2 className="xg-items-title">
                            {t("storeExchange.returnedProductTitle")}
                          </h2>
                        </div>
                        <div className="xg-items-table">
                          {(returnLookupResult.lines || []).map(renderReturnableRow)}
                        </div>
                      </section>

                      <section className="xg-items-card xg-items-card--replacement">
                        <div className="xg-items-card__head">
                          <h2 className="xg-items-title">{t("storeExchange.newProductTitle")}</h2>
                        </div>

                        <div className="xg-replacement-modes" role="tablist">
                          <button
                            type="button"
                            role="tab"
                            aria-selected={replacementMode === "scan"}
                            className={`xg-mode-tab${replacementMode === "scan" ? " is-active" : ""}`}
                            onClick={() => selectReplacementMode("scan")}
                          >
                            <i className="tim-icons icon-tag" aria-hidden="true" />
                            {t("storeSales.scanModeTab")}
                          </button>
                          <button
                            type="button"
                            role="tab"
                            aria-selected={replacementMode === "search"}
                            className={`xg-mode-tab${replacementMode === "search" ? " is-active" : ""}`}
                            onClick={() => selectReplacementMode("search")}
                          >
                            <i className="tim-icons icon-zoom-split" aria-hidden="true" />
                            {t("storeSales.searchModeTab")}
                          </button>
                        </div>

                        {replacementMode === "scan" ? (
                          <Form
                            className="xg-search-form"
                            onSubmit={handleReplacementScanSubmit}
                          >
                            <Label for="exchange-replacement-scan" className="sr-only">
                              {t("storeExchange.scanReplacementBarcode")}
                            </Label>
                            <div className="xg-field-wrap">
                              <div className="xg-field-control">
                                <i
                                  className="tim-icons icon-tag xg-field-icon xg-field-icon--scan"
                                  aria-hidden="true"
                                />
                                <Input
                                  id="exchange-replacement-scan"
                                  innerRef={replacementScanInputRef}
                                  type="text"
                                  autoComplete="off"
                                  className="xg-field-input xg-scan-input"
                                  placeholder={
                                    canScanReplacement
                                      ? t("storeExchange.scanNewProductPlaceholder")
                                      : t("storeExchange.chooseReturnedProductFirst")
                                  }
                                  value={replacementScanValue}
                                  onChange={(event) =>
                                    setReplacementScanValue(event.target.value)
                                  }
                                  disabled={
                                    !canScanReplacement ||
                                    isScanningReplacement ||
                                    isCreatingExchange
                                  }
                                />
                              </div>
                              <Button
                                color="primary"
                                type="submit"
                                className="xg-field-submit"
                                disabled={
                                  !canScanReplacement ||
                                  !replacementScanValue.trim() ||
                                  isScanningReplacement ||
                                  isCreatingExchange
                                }
                              >
                                {isScanningReplacement
                                  ? t("commerceCommon.scanning")
                                  : t("storeExchange.addReplacement")}
                              </Button>
                            </div>
                            {!canScanReplacement && (
                              <p className="xg-scan-hint-text">
                                {t("storeExchange.chooseReturnedProductFirst")}
                              </p>
                            )}
                          </Form>
                        ) : (
                          <Form
                            className="xg-search-form"
                            onSubmit={handleReplacementSearchSubmit}
                          >
                            <Label for="exchange-replacement-search" className="sr-only">
                              {t("storeExchange.replacementSearchPlaceholder")}
                            </Label>
                            <div className="xg-field-wrap">
                              <div className="xg-field-control">
                                <i
                                  className="tim-icons icon-zoom-split xg-field-icon"
                                  aria-hidden="true"
                                />
                                <Input
                                  id="exchange-replacement-search"
                                  innerRef={replacementSearchInputRef}
                                  type="text"
                                  autoComplete="off"
                                  className="xg-field-input"
                                  placeholder={
                                    canScanReplacement
                                      ? t("storeExchange.replacementSearchPlaceholder")
                                      : t("storeExchange.chooseReturnedProductFirst")
                                  }
                                  value={replacementSearchValue}
                                  onChange={(event) =>
                                    setReplacementSearchValue(event.target.value)
                                  }
                                  disabled={
                                    !canScanReplacement ||
                                    isSearchingReplacement ||
                                    isCreatingExchange
                                  }
                                />
                              </div>
                              <Button
                                color="primary"
                                type="submit"
                                className="xg-field-submit"
                                disabled={
                                  !canScanReplacement ||
                                  !replacementSearchValue.trim() ||
                                  isSearchingReplacement ||
                                  isCreatingExchange
                                }
                              >
                                {isSearchingReplacement
                                  ? t("commerceCommon.searching")
                                  : t("commerceCommon.search")}
                              </Button>
                            </div>
                            {!canScanReplacement && (
                              <p className="xg-scan-hint-text">
                                {t("storeExchange.chooseReturnedProductFirst")}
                              </p>
                            )}

                            {replacementSearchResults.length > 0 && (
                              <div className="xg-replacement-results">
                                {replacementSearchPagination?.hasMore && (
                                  <p className="xg-replacement-results__count">
                                    {t("storeExchange.replacementSearchMore")
                                      .replace("{shown}", replacementSearchResults.length)
                                      .replace("{total}", replacementSearchPagination.total)}
                                  </p>
                                )}
                                <div className="sir-list">
                                  {replacementSearchResults.map(renderReplacementSearchItem)}
                                </div>
                              </div>
                            )}
                          </Form>
                        )}

                        {replacementBasket.length > 0 && (
                          <div className="xg-items-table xg-items-table--replacement">
                            {replacementBasket.map(renderReplacementRow)}
                          </div>
                        )}
                      </section>

                      <div className="exchange-page-checkout-mobile">
                        {renderSettlementPanel(false)}
                      </div>
                    </>
                  ) : (
                    <div className="sp-empty sp-empty--compact">
                      <div className="sp-empty-icon">
                        <i className="tim-icons icon-zoom-split" />
                      </div>
                      <div className="sp-empty-title">{t("storeReturn.readyToLookup")}</div>
                      <p className="sp-empty-desc">
                        {t("storeReturn.aggregateOrScanHint")}
                      </p>
                    </div>
                  )}
                </div>

                {returnLookupResult ? renderSettlementPanel(true) : null}
              </div>
            </>
          )}
        </CardBody>
      </Card>
    </div>
  );
};

export default ExchangesPage;
